import { get, startCase } from "lodash/fp";

import { RfpData } from "./types";

export interface BenefitPlanValidation {
  planId: string;
  planIdentifier?: string;
  missingFields: string[];
}

export interface MissingFieldValidation {
  coverageId: string;
  coverageType: string;
  benefitPlans: BenefitPlanValidation[];
}

export interface CoverageValidationResult {
  hasErrors: boolean;
  validations: MissingFieldValidation[];
}

export type RequiredField = string | { fieldPath: string; name: string };

export interface CoverageFieldConfig {
  [coverageType: string]: {
    requiredFields: RequiredField[];
  };
}

const NOT_APPLICABLE = "notApplicable";

/**
 * Checks if a value is considered empty
 */
function isEmpty(value: unknown): boolean {
  if (value === null || value === undefined) {
    return true;
  }
  if (typeof value === "string") {
    return value.trim() === "";
  }
  return false;
}

function getBenefitPlanRequiredFields(
  requiredFields: RequiredField[],
): { fieldPath: string; displayName?: string }[] {
  return requiredFields
    .filter((f) => {
      const fieldPath = typeof f === "string" ? f : f.fieldPath;
      return fieldPath.startsWith("benefitPlans.");
    })
    .map((f) => {
      const fieldPath = typeof f === "string" ? f : f.fieldPath;
      const displayName = typeof f === "object" ? f.name : undefined;
      return {
        fieldPath: fieldPath.replace(/^benefitPlans\./, ""),
        displayName,
      };
    });
}

function getBenefitPlanValidations(
  requiredFields: { fieldPath: string; displayName?: string }[],
  benefitPlans: RfpData["coverages"][0]["benefitPlans"],
): BenefitPlanValidation[] | null {
  const plans: BenefitPlanValidation[] = [];
  if (requiredFields.length > 0 && Array.isArray(benefitPlans)) {
    benefitPlans.forEach((benefitPlan, index) => {
      const missingFields: string[] = [];

      requiredFields.forEach((fieldConfig) => {
        const value = get(fieldConfig.fieldPath, benefitPlan) as unknown;

        if (isEmpty(value)) {
          // Use provided display name if available, otherwise apply startCase to field name
          const fieldName =
            fieldConfig.displayName ||
            startCase(fieldConfig.fieldPath.split(".").pop());
          missingFields.push(fieldName);
        }
      });

      if (missingFields.length === 0) {
        return; // No missing fields for this plan
      }

      let planIdentifier: string | undefined;
      // Differentiate plans if there are multiple of the same type
      if (benefitPlans.length > 1) {
        planIdentifier =
          `${benefitPlan.type && benefitPlan.type !== NOT_APPLICABLE ? benefitPlan.type.toLocaleUpperCase() : ""} ${index + 1}`.trim();
      }

      plans.push({
        planId: benefitPlan.id,
        planIdentifier,
        missingFields,
      });
    });
  }
  return plans.length > 0 ? plans : null;
}

/**
 * Validates that coverage fields exist and are not null/empty based on configuration
 * Validates ALL benefit plans for each coverage (handles dual plans like Dental)
 *
 * @param data - The RFP data structure containing coverages
 * @param config - Configuration specifying required fields for each coverage type
 * @returns Validation result with missing fields for each coverage and benefit plan
 *
 * @example
 * const config: CoverageFieldConfig = {
 *   std: {
 *     requiredFields: ['benefitPlans.benefits.benefitAmount', 'benefitPlans.benefits.benefitDuration']
 *   },
 *   dental: {
 *     requiredFields: ['benefitPlans.benefits.networkBenefits', 'benefitPlans.benefits.dependentAgeLimitYears']
 *   }
 * };
 *
 * const result = validateCoverageFields(rfpData, config);
 * // Returns validations with benefitPlans array containing all plans
 */
export function validateCoverageFields(
  data: RfpData,
  config: CoverageFieldConfig,
): CoverageValidationResult {
  const validations: MissingFieldValidation[] = [];

  if (!data.coverages || !Array.isArray(data.coverages)) {
    return {
      hasErrors: false,
      validations: [],
    };
  }

  for (const coverage of data.coverages) {
    const coverageConfig = config[coverage.coverageType];

    if (!coverageConfig) {
      // No validation config for this coverage type
      continue;
    }

    const benefitPlanRequiredFields = getBenefitPlanRequiredFields(
      coverageConfig.requiredFields,
    );

    const benefitPlans = getBenefitPlanValidations(
      benefitPlanRequiredFields,
      coverage.benefitPlans,
    );

    if (benefitPlans) {
      validations.push({
        coverageId: coverage.id,
        coverageType: coverage.type,
        benefitPlans,
      });
    }
  }

  return {
    hasErrors: validations.length > 0,
    validations,
  };
}
